import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { quizAPI } from '../utils/api';
import Button from '../components/Button';
import QuizComponent from '../components/QuizComponent';
import { Link } from 'react-router-dom';

interface Option {
  _id: string;
  text: string;
  isCorrect?: boolean;
  score?: number;
}

interface Question {
  _id: string;
  text?: string;
  question?: string;
  options: Option[];
  explanation?: string;
  points?: number;
}

interface Quiz {
  _id: string;
  title: string;
  description: string;
  questions: Question[];
  category: string;
  difficulty: string;
  timeLimit?: number;
  createdAt: string;
  updatedAt: string;
}

interface QuizResult {
  score: number;
  totalPoints: number;
  percentage: number;
  answers: {
    questionId: string;
    selectedOptionId: string;
    isCorrect: boolean;
    points: number;
  }[];
  timeTaken?: number;
  backendResult?: {
    score: number;
    totalPossibleScore: number;
    percentage: number;
    answersDetails: {
      questionId: string;
      question: string;
      selectedOptionId: string;
      selectedOptionText: string;
      score: number;
      bestOptionId: string;
      bestOptionText: string;
      bestOptionScore: number;
      isOptimal: boolean;
    }[];
    result: {
      title: string;
      description: string;
    };
    recommendedPosts?: any[];
  };
}

const QuizPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);

  useEffect(() => {
    const fetchQuiz = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await quizAPI.getQuizById(id);
        setQuiz(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Kunne ikke indlæse quiz');
      } finally {
        setLoading(false);
      }
    };

    fetchQuiz();
  }, [id]);

  const handleStartQuiz = () => {
    setQuizStarted(true);
    setStartTime(Date.now());
  };

  const handleQuizSubmit = async (answers: { questionId: string; selectedOptionId: string }[]) => {
    if (!quiz || !id) return;

    try {
      setLoading(true);
      
      // Calculate time taken if quiz has a time limit
      const timeTaken = startTime ? Math.floor((Date.now() - startTime) / 1000) : undefined;
      
      // Submit answers to backend
      const response = await quizAPI.submitQuiz(id, answers);
      const backendResult = response.data;
      
      // Use backend results if available
      const result: QuizResult = {
        score: backendResult.score || 0,
        totalPoints: backendResult.totalPossibleScore || quiz.questions.length * 10,
        percentage: backendResult.percentage || 0,
        answers: answers.map(answer => {
          // Find the answer details from backend if available
          const answerDetail = backendResult.answersDetails?.find(
            (a: any) => a.questionId.toString() === answer.questionId && 
                        a.selectedOptionId.toString() === answer.selectedOptionId
          );
          
          return {
            questionId: answer.questionId,
            selectedOptionId: answer.selectedOptionId,
            isCorrect: answerDetail ? answerDetail.isOptimal : false,
            points: answerDetail ? answerDetail.score : 0
          };
        }),
        timeTaken,
        backendResult
      };
      
      setQuizResult(result);
      setQuizCompleted(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke gemme quiz-resultater');
    } finally {
      setLoading(false);
    }
  };

  const handleRetakeQuiz = () => {
    setQuizStarted(true);
    setQuizCompleted(false);
    setQuizResult(null);
    setStartTime(Date.now());
  };

  if (loading && !quiz) {
    return (
      <div className="container-custom py-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-snudefryd-blue mx-auto"></div>
        <p className="mt-4 text-gray-600">Indlæser quiz...</p>
      </div>
    );
  }

  if (error || !quiz) {
    return (
      <div className="container-custom py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-xl font-bold text-red-700 mb-4">Ups!</h3>
          <p className="text-red-600 mb-6">{error || 'Quizzen blev ikke fundet'}</p>
          <Button onClick={() => navigate('/quizzes')} variant="primary">
            Se alle quizzer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-snudefryd-beige min-h-screen py-12">
      <div className="container-custom">
        {!quizStarted ? (
          <div className="bg-white rounded-lg shadow-md p-8 max-w-3xl mx-auto">
            <h1 className="text-3xl font-bold text-center mb-6">{quiz.title}</h1>
            <div className="mb-8">
              <p className="text-gray-700 mb-4">{quiz.description}</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 p-3 rounded-md">
                  <span className="font-semibold">Kategori:</span> {quiz.category}
                </div>
                <div className="bg-gray-50 p-3 rounded-md">
                  <span className="font-semibold">Sværhedsgrad:</span> {quiz.difficulty}
                </div>
                <div className="bg-gray-50 p-3 rounded-md">
                  <span className="font-semibold">Antal spørgsmål:</span> {quiz.questions.length}
                </div>
                {quiz.timeLimit && (
                  <div className="bg-gray-50 p-3 rounded-md">
                    <span className="font-semibold">Tidsbegrænsning:</span> {quiz.timeLimit} minutter
                  </div>
                )}
              </div>
            </div>
            <div className="text-center">
              <Button onClick={handleStartQuiz} variant="primary" size="lg">
                Start Quiz
              </Button>
            </div>
          </div>
        ) : quizCompleted && quizResult ? (
          <div className="bg-white rounded-lg shadow-md p-8 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-6">Quiz Resultater</h2>
            
            <div className="mb-8 text-center">
              <div className="inline-block rounded-full p-6 mb-4 bg-snudefryd-light-purple">
                <span className="text-4xl font-bold text-snudefryd-blue">
                  {quizResult.backendResult?.percentage || Math.round(quizResult.percentage)}%
                </span>
              </div>
              <p className="text-xl mb-2">
                Din score: {quizResult.backendResult?.score || quizResult.score} af {quizResult.backendResult?.totalPossibleScore || quizResult.totalPoints} point
              </p>
              {quizResult.timeTaken && (
                <p className="text-gray-600">
                  Tid brugt: {Math.floor(quizResult.timeTaken / 60)}m {quizResult.timeTaken % 60}s
                </p>
              )}
            </div>
            
            {/* Display backend result */}
            {quizResult.backendResult && quizResult.backendResult.result && (
              <div className="mb-8 p-6 bg-snudefryd-light-purple bg-opacity-20 rounded-lg">
                <h3 className="text-xl font-semibold mb-3">{quizResult.backendResult.result.title}</h3>
                <p className="text-gray-700">{quizResult.backendResult.result.description}</p>
              </div>
            )}
            
            {/* Display recommended posts if available */}
            {quizResult.backendResult?.recommendedPosts && quizResult.backendResult.recommendedPosts.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4">Anbefalede artikler</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {quizResult.backendResult.recommendedPosts.map(post => (
                    <div key={post._id} className="border rounded-lg overflow-hidden shadow-sm">
                      {post.coverImage && (
                        <img 
                          src={post.coverImage} 
                          alt={post.title} 
                          className="w-full h-40 object-cover"
                        />
                      )}
                      <div className="p-4">
                        <h4 className="font-semibold mb-2">{post.title}</h4>
                        <p className="text-sm text-gray-600 mb-3">{post.excerpt}</p>
                        <Link 
                          to={`/blog/${post.slug}`}
                          className="text-snudefryd-blue hover:underline text-sm font-medium"
                        >
                          Læs mere
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4">Gennemgang af svar</h3>
              {quiz.questions.map((question, index) => {
                const answer = quizResult.answers.find(a => a.questionId === question._id);
                // Find the answer details from backend if available
                const answerDetail = quizResult.backendResult?.answersDetails?.find(
                  a => a.questionId.toString() === question._id
                );
                
                const selectedOption = question.options.find(o => o._id === answer?.selectedOptionId);
                const isCorrect = answerDetail ? answerDetail.isOptimal : answer?.isCorrect;
                
                return (
                  <div 
                    key={question._id} 
                    className={`mb-6 p-4 rounded-lg ${
                      isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    }`}
                  >
                    <p className="font-semibold mb-2">
                      {index + 1}. {question.text || question.question}
                    </p>
                    
                    <div className="ml-4 mb-2">
                      <p className="text-sm">
                        <span className="font-medium">Dit svar:</span>{' '}
                        <span className={isCorrect ? 'text-green-600' : 'text-red-600'}>
                          {answerDetail?.selectedOptionText || selectedOption?.text || 'Intet svar'}
                        </span>
                      </p>
                      
                      {!isCorrect && (
                        <p className="text-sm text-green-600">
                          <span className="font-medium">Korrekt svar:</span> {answerDetail?.bestOptionText || 'Ikke tilgængelig'}
                        </p>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-700 mt-2">{question.explanation}</p>
                  </div>
                );
              })}
            </div>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button onClick={handleRetakeQuiz} variant="primary">
                Tag quizzen igen
              </Button>
              <Button onClick={() => navigate('/quizzes')} variant="outline">
                Se alle quizzer
              </Button>
            </div>
          </div>
        ) : (
          <QuizComponent 
            quiz={quiz} 
            onSubmit={handleQuizSubmit} 
            timeLimit={quiz.timeLimit}
          />
        )}
      </div>
    </div>
  );
};

export default QuizPage; 